/**
 * Resolves Alkagi CSS custom properties to concrete color strings the
 * Canvas 2D API can consume. The canvas API only accepts literal color
 * values, so we read computed styles once per render pass.
 */

export interface AlkagiCanvasTokens {
  readonly boardFill: string;
  readonly boardFrame: string;
  readonly gridLine: string;
  readonly stoneBlack: string;
  readonly stoneWhite: string;
  readonly stoneWhiteBorder: string;
  readonly stoneShadow: string;
  readonly stoneSelected: string;
  readonly aimLine: string;
  readonly aimDot: string;
}

const TOKEN_NAMES = {
  boardFill: "--cn-alkagi-board-fill",
  boardFrame: "--cn-alkagi-board-frame",
  gridLine: "--cn-alkagi-grid-line",
  stoneBlack: "--cn-alkagi-stone-black",
  stoneWhite: "--cn-alkagi-stone-white",
  stoneWhiteBorder: "--cn-alkagi-stone-white-border",
  stoneShadow: "--cn-alkagi-stone-shadow",
  stoneSelected: "--cn-alkagi-stone-selected",
  aimLine: "--cn-alkagi-aim-line",
  aimDot: "--cn-alkagi-aim-dot",
} as const;

const FALLBACK: AlkagiCanvasTokens = {
  boardFill: "#d4ad6b",
  boardFrame: "#6b5b3e",
  gridLine: "rgba(107, 91, 62, 0.45)",
  stoneBlack: "#1e293b",
  stoneWhite: "#f0ede8",
  stoneWhiteBorder: "rgba(0, 0, 0, 0.15)",
  stoneShadow: "rgba(0, 0, 0, 0.25)",
  stoneSelected: "rgba(59, 130, 246, 0.6)",
  aimLine: "rgba(245, 158, 11, 0.55)",
  aimDot: "rgba(245, 158, 11, 0.85)",
} as const;

export function resolveAlkagiCanvasTokens(
  element: Element | null,
): AlkagiCanvasTokens {
  if (!element || typeof window === "undefined") return FALLBACK;
  const styles = window.getComputedStyle(element);
  const read = (name: string, fallback: string): string => {
    const value = styles.getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
  };
  return {
    boardFill: read(TOKEN_NAMES.boardFill, FALLBACK.boardFill),
    boardFrame: read(TOKEN_NAMES.boardFrame, FALLBACK.boardFrame),
    gridLine: read(TOKEN_NAMES.gridLine, FALLBACK.gridLine),
    stoneBlack: read(TOKEN_NAMES.stoneBlack, FALLBACK.stoneBlack),
    stoneWhite: read(TOKEN_NAMES.stoneWhite, FALLBACK.stoneWhite),
    stoneWhiteBorder: read(
      TOKEN_NAMES.stoneWhiteBorder,
      FALLBACK.stoneWhiteBorder,
    ),
    stoneShadow: read(TOKEN_NAMES.stoneShadow, FALLBACK.stoneShadow),
    stoneSelected: read(TOKEN_NAMES.stoneSelected, FALLBACK.stoneSelected),
    aimLine: read(TOKEN_NAMES.aimLine, FALLBACK.aimLine),
    aimDot: read(TOKEN_NAMES.aimDot, FALLBACK.aimDot),
  };
}
